import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import {
  Add as AddIcon,
  CheckCircleOutline as CheckIcon,
  DeleteOutline as DeleteIcon,
  ErrorOutline as ErrorIcon,
  PaidOutlined as PaidIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";
import axios from "axios";
import { getAuthHeaders } from "../../utils/auth";

const STATUS_LABELS = {
  signed_with_pass: { label: "Z karnetu", color: "success" },
  signed_pass_exhausted: { label: "Z karnetu — ostatnie wejście", color: "warning" },
  signed_no_pass_unpaid: { label: "Bez karnetu — DŁUG", color: "error" },
  signed_on_site_unpaid: { label: "Płatne na miejscu", color: "default" },
  signed_split_pending: { label: "Płatność podzielona — oczekująca", color: "info" },
  signed_online_pending: { label: "Płatność online — oczekująca", color: "info" },
  signed_free: { label: "Darmowe", color: "success" },
  skipped_payment_required: { label: "Pominięto — wymagana płatność online", color: "info" },
  waitlisted: { label: "Lista oczekujących", color: "default" },
  failed: { label: "Błąd", color: "error" },
  already_enrolled: { label: "Już zapisany", color: "default" },
  manually_paid: { label: "Oznaczone jako opłacone", color: "success" },
  removed: { label: "Wypisano z zajęć", color: "default" },
};

const formatLessonStart = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  const date = d.toLocaleDateString("pl-PL");
  const time = d.toLocaleTimeString("pl-PL", { hour: "2-digit", minute: "2-digit" });
  return `${date} ${time}`;
};

const groupByUser = (perLessonReports) => {
  const map = new Map();
  perLessonReports.forEach((lessonReport) => {
    (lessonReport.results || []).forEach((row) => {
      if (!map.has(row.user_id)) {
        map.set(row.user_id, {
          user_id: row.user_id,
          user_full_name: row.user_full_name,
          user_email: row.user_email,
          rows: [],
        });
      }
      map.get(row.user_id).rows.push({
        ...row,
        lesson_id: lessonReport.lesson_id,
        lesson_start: lessonReport.lesson_start,
        lesson_type_name: lessonReport.lesson_type_name,
      });
    });
  });
  return Array.from(map.values()).sort((a, b) =>
    (a.user_full_name || "").localeCompare(b.user_full_name || "", "pl")
  );
};

const summarize = (perUser, overrides = {}) => {
  let unpaidCount = 0;
  let unpaidAmount = 0;
  let exhaustedUsers = 0;
  let waitlisted = 0;
  let failed = 0;
  perUser.forEach((u) => {
    let userExhausted = false;
    u.rows.forEach((r) => {
      const key = `${r.lesson_id}:${r.user_id}`;
      const override = overrides[key] || {};
      const effectiveStatus = override.status || r.status;
      const effectiveAmount = override.amount_owed !== undefined ? override.amount_owed : r.amount_owed;
      if (effectiveStatus === "signed_no_pass_unpaid") {
        unpaidCount += 1;
        unpaidAmount += effectiveAmount || 0;
      }
      if (effectiveStatus === "signed_pass_exhausted") userExhausted = true;
      if (effectiveStatus === "waitlisted") waitlisted += 1;
      if (effectiveStatus === "failed") failed += 1;
    });
    if (userExhausted) exhaustedUsers += 1;
  });
  return { unpaidCount, unpaidAmount, exhaustedUsers, waitlisted, failed };
};

const CreationReportDialog = ({ open, onClose, perLessonReports }) => {
  const perUser = useMemo(() => groupByUser(perLessonReports || []), [perLessonReports]);

  const [rowOverrides, setRowOverrides] = useState({}); // key=lesson_id:user_id -> {note, status, amount_owed}
  const [rowBusy, setRowBusy] = useState({});
  const [addPassFor, setAddPassFor] = useState(null); // user object

  const summary = useMemo(() => summarize(perUser, rowOverrides), [perUser, rowOverrides]);

  useEffect(() => {
    if (!open) {
      setRowOverrides({});
      setRowBusy({});
      setAddPassFor(null);
    }
  }, [open]);

  const rowKey = (lessonId, userId) => `${lessonId}:${userId}`;
  const setBusy = (key, val) => setRowBusy((p) => ({ ...p, [key]: val }));
  const overrideRow = (key, patch) => setRowOverrides((p) => ({ ...p, [key]: { ...(p[key] || {}), ...patch } }));

  const handleMarkPaid = async (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    setBusy(key, "paid");
    try {
      const headers = await getAuthHeaders();
      await axios.patch(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/lesson/${row.lesson_id}/users/${row.user_id}/payment`,
        { payed: true },
        { headers }
      );
      overrideRow(key, { note: "Oznaczone jako opłacone", status: "manually_paid", amount_owed: 0 });
    } catch (e) {
      overrideRow(key, { note: `Błąd: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(key, null);
    }
  };

  const handleRemove = async (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    if (!window.confirm(`Wypisać ${row.user_full_name} z zajęć ${row.lesson_type_name}?`)) return;
    setBusy(key, "remove");
    try {
      const headers = await getAuthHeaders();
      await axios.delete(`${process.env.REACT_APP_BACKEND_ADDRESS}/user_from_lesson/`, {
        headers,
        data: { lesson_id: row.lesson_id, user_id: row.user_id },
      });
      overrideRow(key, { note: "Wypisano z zajęć", status: "removed", amount_owed: 0 });
    } catch (e) {
      overrideRow(key, { note: `Błąd: ${e?.response?.data?.detail || e.message}` });
    } finally {
      setBusy(key, null);
    }
  };

  const renderActions = (row) => {
    const key = rowKey(row.lesson_id, row.user_id);
    const busy = rowBusy[key];
    const override = rowOverrides[key] || {};
    const effectiveStatus = override.status || row.status;
    const showActions = ["signed_no_pass_unpaid", "signed_pass_exhausted", "signed_on_site_unpaid"].includes(effectiveStatus);
    if (!showActions) return null;
    return (
      <Stack direction="row" spacing={0.5} alignItems="center">
        {effectiveStatus === "signed_no_pass_unpaid" && (
          <Tooltip title="Oznacz jako opłacone">
            <span>
              <IconButton size="small" onClick={() => handleMarkPaid(row)} disabled={!!busy}>
                {busy === "paid" ? <CircularProgress size={16} /> : <PaidIcon fontSize="small" />}
              </IconButton>
            </span>
          </Tooltip>
        )}

        <Tooltip title="Wypisz z zajęć">
          <span>
            <IconButton size="small" onClick={() => handleRemove(row)} disabled={!!busy}>
              {busy === "remove" ? <CircularProgress size={16} /> : <DeleteIcon fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
      </Stack>
    );
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogTitle>
          Raport tworzenia zajęć
          <Typography variant="body2" color="text.secondary">
            Sprawdź jak system zapisał użytkowników i podejmij akcje, jeśli to potrzebne.
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
            <Alert severity="info" sx={{ flex: 1 }}>
              <AlertTitle>Podsumowanie</AlertTitle>
              Utworzono {perLessonReports?.length || 0} zajęć dla {perUser.length} użytkowników.
            </Alert>
            {summary.unpaidCount > 0 && (
              <Alert severity="error" sx={{ flex: 1 }}>
                <AlertTitle>Niezapłacone zapisy</AlertTitle>
                {summary.unpaidCount} zapisów bez karnetu — łączny dług:{" "}
                <b>{summary.unpaidAmount} zł</b>.
              </Alert>
            )}
            {summary.exhaustedUsers > 0 && (
              <Alert severity="warning" sx={{ flex: 1 }}>
                <AlertTitle>Wyczerpane karnety</AlertTitle>
                {summary.exhaustedUsers} użytkownikom karnet wyczerpał się podczas zapisów.
              </Alert>
            )}
            {summary.waitlisted > 0 && (
              <Alert severity="info" sx={{ flex: 1 }}>
                <AlertTitle>Lista oczekujących</AlertTitle>
                {summary.waitlisted} zapisów trafiło na listę oczekujących.
              </Alert>
            )}
          </Stack>

          {perUser.length === 0 ? (
            <Alert severity="info">Brak użytkowników do raportowania.</Alert>
          ) : (
            perUser.map((user) => (
              <Box key={user.user_id} sx={{ mb: 3 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {user.user_full_name}{" "}
                    <Typography component="span" variant="body2" color="text.secondary">
                      ({user.user_email || "brak email"})
                    </Typography>
                  </Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    variant="outlined"
                    onClick={() => setAddPassFor(user)}
                  >
                    Dodaj karnet
                  </Button>
                </Stack>
                <Table size="small" sx={{ mt: 1 }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Data zajęć</TableCell>
                      <TableCell>Typ</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Karnet</TableCell>
                      <TableCell>Dług</TableCell>
                      <TableCell align="right">Akcje</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {user.rows
                      .slice()
                      .sort((a, b) => new Date(a.lesson_start) - new Date(b.lesson_start))
                      .map((row) => {
                        const key = rowKey(row.lesson_id, row.user_id);
                        const override = rowOverrides[key] || {};
                        const status = override.status || row.status;
                        const amount = override.amount_owed != null ? override.amount_owed : row.amount_owed;
                        const cfg = STATUS_LABELS[status] || { label: status, color: "default" };
                        return (
                          <TableRow key={key}>
                            <TableCell>{formatLessonStart(row.lesson_start)}</TableCell>
                            <TableCell>{row.lesson_type_name}</TableCell>
                            <TableCell>
                              <Chip
                                size="small"
                                label={cfg.label}
                                color={cfg.color}
                                icon={
                                  status === "signed_no_pass_unpaid" ? (
                                    <ErrorIcon />
                                  ) : status === "signed_pass_exhausted" ? (
                                    <WarningIcon />
                                  ) : status === "signed_with_pass" ? (
                                    <CheckIcon />
                                  ) : undefined
                                }
                              />
                              {override.note && (
                                <Typography variant="caption" display="block" color="text.secondary">
                                  {override.note}
                                </Typography>
                              )}
                              {row.error && (
                                <Typography variant="caption" display="block" color="error">
                                  {row.error}
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {row.pass_type_name ? (
                                <>
                                  <Typography variant="body2">{row.pass_type_name}</Typography>
                                  {row.pass_remaining_after != null && (
                                    <Typography variant="caption" color="text.secondary">
                                      Pozostało: {row.pass_remaining_after}
                                    </Typography>
                                  )}
                                </>
                              ) : (
                                <Typography variant="body2" color="text.secondary">
                                  —
                                </Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              {amount ? <b>{amount} zł</b> : <Typography variant="body2">—</Typography>}
                            </TableCell>
                            <TableCell align="right">{renderActions(row)}</TableCell>
                          </TableRow>
                        );
                      })}
                  </TableBody>
                </Table>
                <Divider sx={{ mt: 2 }} />
              </Box>
            ))
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} variant="contained">
            Zamknij
          </Button>
        </DialogActions>
      </Dialog>

      <AddPassQuickDialog
        user={addPassFor}
        open={!!addPassFor}
        onClose={() => setAddPassFor(null)}
        onAdded={(passInfo) => {
          setAddPassFor(null);
          // Mark every unpaid row of this user with a hint
          perUser
            .find((u) => u.user_id === passInfo.user_id)
            ?.rows.forEach((row) => {
              if (row.status === "signed_no_pass_unpaid" || row.status === "signed_pass_exhausted") {
                overrideRow(rowKey(row.lesson_id, row.user_id), {
                  note: `Dodano karnet „${passInfo.pass_type_name}" — pamiętaj, by przypisać go do zajęć ręcznie.`,
                });
              }
            });
        }}
      />
    </>
  );
};

const AddPassQuickDialog = ({ open, user, onClose, onAdded }) => {
  const [passTypes, setPassTypes] = useState([]);
  const [selectedPassTypeId, setSelectedPassTypeId] = useState("");
  const [payed, setPayed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setSelectedPassTypeId("");
      setPayed(false);
      setError("");
      return;
    }
    (async () => {
      try {
        const headers = await getAuthHeaders();
        const res = await axios.get(`${process.env.REACT_APP_BACKEND_ADDRESS}/pass-type/`, { headers });
        setPassTypes(res.data?.objects || res.data || []);
      } catch (e) {
        setError("Nie udało się pobrać typów karnetów.");
      }
    })();
  }, [open]);

  const handleSubmit = async () => {
    if (!user || !selectedPassTypeId) return;
    const passType = passTypes.find((pt) => pt.id === selectedPassTypeId);
    if (!passType) return;
    setSubmitting(true);
    setError("");
    try {
      const lessonsCount = (passType.lesson_categories_for_pass_type || []).map((cat) => ({
        lesson_category_id: cat.lesson_category_id,
        lessons_to_use: cat.is_unlimited ? 0 : parseInt(cat.lessons_to_use, 10),
        is_unlimited: cat.is_unlimited || false,
      }));
      const headers = await getAuthHeaders();
      await axios.post(
        `${process.env.REACT_APP_BACKEND_ADDRESS}/pass_to_user/`,
        {
          pass_type_name: passType.name,
          user_id: user.user_id,
          lesson_categories: lessonsCount,
          valid_from: new Date().toISOString(),
          valid_until: null, // backend auto-calculates from pass type defaults
          payed,
          created_by_admin: true,
        },
        { headers }
      );
      onAdded({ user_id: user.user_id, pass_type_name: passType.name });
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się dodać karnetu.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Dodaj karnet — {user?.user_full_name}</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <FormControl fullWidth sx={{ mt: 1 }}>
          <InputLabel id="quick-pass-type-label">Typ karnetu</InputLabel>
          <Select
            labelId="quick-pass-type-label"
            label="Typ karnetu"
            value={selectedPassTypeId}
            onChange={(e) => setSelectedPassTypeId(e.target.value)}
          >
            {passTypes.map((pt) => (
              <MenuItem key={pt.id} value={pt.id}>
                {pt.name} {pt.price ? `— ${pt.price} zł` : ""}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl sx={{ mt: 2 }}>
          <Stack direction="row" alignItems="center" spacing={1}>
            <input
              id="quick-pass-payed"
              type="checkbox"
              checked={payed}
              onChange={(e) => setPayed(e.target.checked)}
            />
            <label htmlFor="quick-pass-payed">Karnet jest opłacony</label>
          </Stack>
        </FormControl>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={submitting}>
          Anuluj
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={!selectedPassTypeId || submitting}
        >
          {submitting ? <CircularProgress size={18} /> : "Dodaj karnet"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CreationReportDialog;
